import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Work from '../../../interfaces/work';
import Event from '../../../interfaces/event';
import Field from '../../../interfaces/field';
import { useService } from '../../../services/services';
import DashboardCard from '../components/dashboard-card';
import DashboardTitle from '../components/dashboard-title';
import DashboardLoading from '../components/dashboard-loading';
import Accordion, {
  AccordionItem,
  PlainAccordionItemBody,
  PlainAccordionItemHeader,
} from '../../../components/common/accordion';
import DayTitle from './day-title';
import DayInfo from './day-info';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import css from './index.module.css';
import { DefaultFormats } from '../../../enums/default-formats';

const FORMAT = 'MM.DD.YYYY';

export default function Calendar() {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.dashboard.calendar.'.concat(key)),
    [t]
  );
  const { farmId, season } = useSelector((state: RootState) => ({
    farmId: state.global?.farm?.id,
    season: state.global?.season,
  }));
  const { workService, eventService } = useService();

  const [isLoading, setIsLoading] = useState(true);
  const [today] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState([]);
  const [currentDay, setCurrentDay] = useState(new Date());
  const getFrom = (date: Date) => {
    const prevMonday = new Date(date);
    prevMonday.setDate(prevMonday.getDate() - ((prevMonday.getDay() + 6) % 7));
    return prevMonday;
  };
  const getTo = (date: Date) => {
    const nextSunday = new Date(date);
    nextSunday.setDate(nextSunday.getDate() + ((7 - nextSunday.getDay()) % 7));
    return nextSunday;
  };
  const [from, setFrom] = useState(getFrom(today));
  const [to, setTo] = useState(getTo(today));
  const getPeriod = useCallback(() => {
    const dateFrom = getFrom(currentDay);
    const dateTo = getTo(currentDay);
    const fromDate = `${dateFrom.getDate()}. ${tp(
      'months.' + (dateFrom.getMonth() + 1)
    )}`;
    const toDate = `${dateTo.getDate()}. ${tp(
      'months.' + (dateTo.getMonth() + 1)
    )} ${dateTo.getFullYear()}`;
    return `${fromDate} - ${toDate}`;
  }, [currentDay, tp]);
  const [period, setPeriod] = useState(getPeriod());

  useEffect(() => {
    const subscription = forkJoin([
      workService.worksInPeriod(
        farmId,
        season,
        from.toLocaleDateString(DefaultFormats.DATE_FILTER),
        to.toLocaleDateString(DefaultFormats.DATE_FILTER)
      ),
      eventService.public(
        from.toLocaleDateString(DefaultFormats.DATE_FILTER),
        to.toLocaleDateString(DefaultFormats.DATE_FILTER)
      ),
    ]).subscribe(([works, events]: [Work[], Event[]]) => {
      const currentWeek = [];
      const fromDate = new Date(from);

      works.forEach((work: Work) => {
        work.treated_area = work.fields.reduce((sum: number, field: Field) => {
          return sum * 1 + field.pivot.treated_area * 1;
        }, 0);
      });

      events.forEach((event: Event) => {
        event.time = moment.utc(event.time).format('HH:mm');
      });

      for (let i = 0; i < 7; i++) {
        const day = moment(new Date(fromDate).setDate(fromDate.getDate() + i));
        const isToday = day.format(FORMAT) === moment(today).format(FORMAT);
        const { dayName, date, month } = {
          dayName: isToday ? 'today' : day.day() ? day.day() : 7,
          date: day.date(),
          month: day.month() + 1,
        };
        currentWeek.push({
          isToday,
          title: `${tp('days.' + dayName)} ${date}. ${tp('months.' + month)}`,
          events: events.filter(
            (event: Event) =>
              moment(event.date).format(FORMAT) === day.format(FORMAT)
          ),
          works: works.filter(
            (work: Work) =>
              moment(work.date).format(FORMAT) === day.format(FORMAT)
          ),
        });
      }

      setCurrentWeek(currentWeek);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [tp, workService, eventService, from, to, farmId, season, today]);

  const prevWeek = () => {
    if (isLoading) return;

    setCurrentDay(new Date(currentDay.setDate(currentDay.getDate() - 7)));
    setFrom(getFrom(currentDay));
    setTo(getTo(currentDay));
    setPeriod(getPeriod());
    setIsLoading(true);
  };

  const nextWeek = () => {
    if (isLoading) return;

    setCurrentDay(new Date(currentDay.setDate(currentDay.getDate() + 7)));
    setFrom(getFrom(currentDay));
    setTo(getTo(currentDay));
    setPeriod(getPeriod());
    setIsLoading(true);
  };

  const controls = (
    <>
      <span onClick={prevWeek} className={css['control']}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </span>
      <span onClick={nextWeek} className={css['control']}>
        <FontAwesomeIcon icon={faChevronRight} />
      </span>
    </>
  );

  return (
    <div className="mb-4">
      <DashboardCard>
        <DashboardTitle title={tp('heading')} elements={controls} />

        <h2 className={css['period']}>{period}</h2>

        {isLoading ? (
          <DashboardLoading name="dashboard-calendar-loading" />
        ) : (
          <Accordion>
            <ul
              className={css['list']}
              data-name="dashboard-calendar-week-list"
            >
              {currentWeek.map(({ isToday, title, events, works }, index) => (
                <li
                  key={`day-${index}-${isToday}`}
                  className={isToday ? css['today'] : ''}
                  data-has-event={!!events.length}
                  data-has-work={!!works.length}
                >
                  <AccordionItem
                    header={
                      <PlainAccordionItemHeader>
                        <DayTitle events={events} title={title} works={works} />
                      </PlainAccordionItemHeader>
                    }
                    body={
                      <PlainAccordionItemBody>
                        {events.length || works.length ? (
                          <DayInfo events={events} works={works} />
                        ) : null}
                      </PlainAccordionItemBody>
                    }
                    opened={isToday}
                  />
                </li>
              ))}
            </ul>
          </Accordion>
        )}
      </DashboardCard>
    </div>
  );
}
